// Pure decision logic for the API access page. Imports neither React nor axios, so the
// Playwright unit-testing runner can exercise it directly.

export interface LocalityOption {
  id: number
  name: string
}

export interface LocalityAccess {
  allLocalities: boolean
  localityIds: number[]
}

export interface CreateApiKeyRequest {
  name: string
  allLocalities: boolean
  localityIds: number[]
}

export interface SetLocalitiesRequest {
  allLocalities: boolean
  localityIds: number[]
}

const grantedIds = (access: LocalityAccess): number[] => (access.allLocalities ? [] : access.localityIds)

export const initialAccessForm = (apiKey: LocalityAccess): LocalityAccess => ({
  allLocalities: apiKey.allLocalities,
  localityIds: apiKey.localityIds,
})

export const buildCreateApiKeyRequest = (name: string, access: LocalityAccess): CreateApiKeyRequest => ({
  name,
  allLocalities: access.allLocalities,
  localityIds: grantedIds(access),
})

export const buildSetLocalitiesRequest = (access: LocalityAccess): SetLocalitiesRequest => ({
  allLocalities: access.allLocalities,
  localityIds: grantedIds(access),
})

// Known localities are named and sorted alphabetically; a grant whose locality no longer
// exists in the customer's list is shown as "#<id>" rather than silently dropped, appended
// after the named ones.
export const describeGrants = (localityIds: number[], localityList: LocalityOption[]): string => {
  const nameById = new Map(localityList.map(l => [l.id, l.name]))
  const named: string[] = []
  const unnamed: number[] = []

  localityIds.forEach(id => {
    const name = nameById.get(id)
    if (name) {
      named.push(name)
    } else {
      unnamed.push(id)
    }
  })

  named.sort((a, b) => a.localeCompare(b))
  unnamed.sort((a, b) => a - b)

  return [...named, ...unnamed.map(id => `#${id}`)].join(', ')
}

export const describeAccess = (
  apiKey: LocalityAccess,
  localityList: LocalityOption[],
  labels: { allLocalities: string },
): string => (apiKey.allLocalities ? labels.allLocalities : describeGrants(apiKey.localityIds, localityList))
